import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, User as UserIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import userService from '../../services/userService';
import ThumbnailImage from '../ThumbnailImage';

/**
 * Kompakte CTA-Sektion für die DartCloud Spielerprofile (Startseite).
 * Pendant zur Flutter-Komponente `home_profile_cta.dart`:
 * - Nicht angemeldet: Hinweis + Button zur Anmeldung/Registrierung.
 * - Angemeldet: Begrüßung mit Profilbild + Button zum eigenen Spielerprofil.
 * Schlanke Pill-Zeile über die volle Breite der Hauptspalte.
 */
const HomeProfileCta: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const [imgError, setImgError] = useState(false);

  const isLoggedIn = isAuthenticated && Boolean(user);
  const username = user?.username ?? null;

  const title = isLoggedIn ? `Hey ${username ?? 'Spieler'}!` : 'Dein Spielerprofil';
  const subtitle = isLoggedIn
    ? 'Dein DartCloud Spielerprofil wartet auf dich.'
    : 'Statistiken, Setup & Community – kostenlos dabei sein.';
  const buttonLabel = isLoggedIn ? 'Zum Profil' : 'Anmelden';
  const target = isLoggedIn && username ? `/community/profil/${username}` : '/login';

  const avatarUrl = userService.getFileUrl(user?.profileImage);
  const showImage = isLoggedIn && Boolean(avatarUrl) && !imgError;

  return (
    <section aria-label="Spielerprofil">
      <Link
        to={target}
        className="group flex w-full items-center gap-3 rounded-2xl border border-[#3fced1]/35 bg-gradient-to-r from-[#11282a] to-zinc-900 px-3.5 py-2.5 shadow-[0_4px_16px_rgba(63,206,210,0.08)] transition-colors hover:border-[#3fced1]/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3fced1]/60"
      >
        {/* Rundes Profilbild mit türkisem Ring (Fallback: Icon-Badge) */}
        <div className="h-11 w-11 shrink-0 overflow-hidden rounded-full border-[1.5px] border-[#3fced1]/60 bg-zinc-800">
          {showImage ? (
            // 44px-Avatar → 100er-Thumbnail statt Original (Fallback: Original)
            <ThumbnailImage
              src={avatarUrl as string}
              thumbSize={100}
              alt=""
              loading="lazy"
              onError={() => setImgError(true)}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#3fced1] to-[#32b4b9]">
              <UserIcon size={22} className="text-black" />
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="truncate text-[14.5px] font-extrabold tracking-tight text-white">
            {title}
          </p>
          <p className="mt-0.5 truncate text-xs text-gray-400">{subtitle}</p>
        </div>

        {/* Türkiser Pill-Button */}
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#3fced1] px-3.5 py-2 text-xs font-bold text-black shadow-[0_2px_10px_rgba(63,206,210,0.35)] transition-transform group-hover:scale-[1.03]">
          {buttonLabel}
          <ArrowRight size={14} />
        </span>
      </Link>
    </section>
  );
};

export default HomeProfileCta;
